import { VirtualGeometryResolver } from './geometry-resolvers/virtual-geometry-resolver';
import { HighlightsRenderer } from './highlights-renderer';
import { type GeometryResolver } from './types';

export interface HighlightsControllerOptions {
  id: string;
}

export class HighlightsController {
  #container: HTMLElement;

  #layers = new Set<HighlightsRenderer>();
  #resolver: GeometryResolver;

  // Track the observer and the width internally
  #resizeObserver: ResizeObserver | null = null;
  #lastWidth: number | null = null;

  constructor(resolver: GeometryResolver, options: HighlightsControllerOptions) {
    this.#resolver = resolver;

    this.#container = document.createElement('div');
    this.#container.id = options.id;

    this.#container.style.position = 'absolute';
    this.#container.style.top = '0';
    this.#container.style.left = '0';
    this.#container.style.pointerEvents = 'none';
  }

  /**
   * Attaches the highlights container to the scroll container.
   * MUST be called by the consumer after the DOM is ready.
   *
   * @param resizeTarget Optional. The specific DOM element to observe for width changes.
   * Defaults to the scroll container if not provided.
   */
  public mount(resizeTarget?: HTMLElement | null) {
    const parent = this.#resolver.getScrollContainer();

    if (parent) {
      if (this.#container.parentElement === parent) {
        return;
      }

      parent.style.position = 'relative';
      parent.appendChild(this.#container);

      // Autonomous Resize Management
      const targetToObserve = resizeTarget || parent;

      this.#lastWidth = targetToObserve.clientWidth;

      // Disconnect any existing observer just in case mount() is called multiple times
      if (this.#resizeObserver) {
        this.#resizeObserver.disconnect();
      }

      this.#resizeObserver = new ResizeObserver(() => {
        const newWidth = targetToObserve.clientWidth;

        // Only clear cache if the width changed (Column Resize)
        if (newWidth !== this.#lastWidth) {
          this.#lastWidth = newWidth;
          this.clearVirtualGeometryCache();
        }

        // Tell all registered layers to redraw to stick to the moving cells
        this.#layers.forEach((layer) => layer.redrawAll());
      });

      this.#resizeObserver.observe(targetToObserve);
    }
  }

  /**
   * Factory: Creates a rendering layer.
   */
  public createLayer(): HighlightsRenderer {
    const layer = new HighlightsRenderer(this.#container, this.#resolver);

    this.#layers.add(layer);

    return layer;
  }

  /**
   * Safely destroys a specific layer and removes its DOM elements.
   */
  public removeLayer(layer: HighlightsRenderer) {
    if (this.#layers.has(layer)) {
      layer.dispose();
      this.#layers.delete(layer);
    }
  }

  /**
   * Completely tears down the controller, destroying all layers,
   * disconnecting observers, and removing the root container from the DOM.
   */
  public dispose() {
    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
      this.#resizeObserver = null;
    }

    this.#layers.forEach((layer) => layer.dispose());
    this.#layers.clear();

    if (this.#container.parentElement) {
      this.#container.parentElement.removeChild(this.#container);
    }
  }

  /**
   * Returns the scroll container which is possibly null if the react app
   * has not mounted.
   */
  public getScrollContainer(): HTMLElement | null {
    return this.#resolver.getScrollContainer();
  }

  /**
   * Clears phantom geometry caches when the injected resolver is a
   * `VirtualGeometryResolver`. No-op for other implementations.
   */
  public clearVirtualGeometryCache() {
    if (this.#resolver instanceof VirtualGeometryResolver) {
      this.#resolver.clearCache();
    }
  }

  /**
   * Returns the highlights container.
   */
  public getContainer(): HTMLElement {
    return this.#container;
  }
}
